import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  Country,
  CreateProjectInput,
  ProjectDetail,
  ProjectListRow,
  ProjectStatus,
  UpdateProjectInput,
  UUID,
} from '@qb/shared';
import { projectsApi } from '@/api/projects';

/** Projects list + edit form. Mirrors the companies store. */
export const useProjectsStore = defineStore('projects', () => {
  const list = ref<ProjectListRow[]>([]);
  const total = ref(0);
  const loadingList = ref(false);

  const status = ref<ProjectStatus>('active');
  const country = ref<Country>('IL');
  const search = ref('');

  const current = ref<ProjectDetail | null>(null);
  const loadingCurrent = ref(false);
  const saving = ref(false);

  const isEmpty = computed(() => !loadingList.value && list.value.length === 0);

  async function fetchList(): Promise<void> {
    loadingList.value = true;
    try {
      const data = await projectsApi.list({
        status: status.value,
        country: country.value,
        search: search.value.trim() || undefined,
      });
      list.value = data.items;
      total.value = data.total;
    } catch {
      list.value = [];
      total.value = 0;
    } finally {
      loadingList.value = false;
    }
  }

  function setStatus(next: ProjectStatus): void {
    if (status.value === next) return;
    status.value = next;
    void fetchList();
  }
  function setCountry(next: Country): void {
    if (country.value === next) return;
    country.value = next;
    void fetchList();
  }
  function setSearch(next: string): void {
    search.value = next;
    void fetchList();
  }

  async function fetchById(id: UUID): Promise<void> {
    loadingCurrent.value = true;
    try {
      current.value = await projectsApi.detail(id);
    } catch {
      current.value = null;
    } finally {
      loadingCurrent.value = false;
    }
  }

  function resetCurrent(): void {
    current.value = null;
  }

  async function create(input: CreateProjectInput): Promise<ProjectDetail> {
    saving.value = true;
    try {
      return await projectsApi.create(input);
    } finally {
      saving.value = false;
    }
  }

  async function update(id: UUID, input: UpdateProjectInput): Promise<ProjectDetail> {
    saving.value = true;
    try {
      const updated = await projectsApi.update(id, input);
      current.value = updated;
      return updated;
    } finally {
      saving.value = false;
    }
  }

  async function setActive(ids: UUID[], isActive: boolean): Promise<void> {
    saving.value = true;
    try {
      await Promise.all(ids.map((id) => projectsApi.update(id, { isActive })));
      await fetchList();
    } finally {
      saving.value = false;
    }
  }

  return {
    list,
    total,
    loadingList,
    status,
    country,
    search,
    current,
    loadingCurrent,
    saving,
    isEmpty,
    fetchList,
    setStatus,
    setCountry,
    setSearch,
    fetchById,
    resetCurrent,
    create,
    update,
    setActive,
  };
});
